import { supabase } from "./supabase";

/**
 * The browser's half of the Gmail connection.
 *
 * There is deliberately not much here. Everything that matters — the client
 * secret, the refresh token, the check on who may be emailed — is in the two
 * Edge Functions, and this file only knows how to ask them. The browser never
 * holds a Google token of any kind.
 */

export interface GmailAccountRow {
  profile_id: string;
  email_address: string;
  connected_at: string;
  last_sent_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
}

/** Where Google sends a person back to. Registered in the Google console. */
export const GOOGLE_RETURN_PATH = "/oauth/google";

export const googleRedirectUri = (): string =>
  `${window.location.origin}${GOOGLE_RETURN_PATH}`;

/**
 * The `state` parameter, kept for the length of the round trip.
 *
 * It is not what decides whose account gets connected — the Edge Function reads
 * that from the caller's own session, so this cannot be used to attach a Google
 * account to somebody else. What it does is prove the code coming back belongs
 * to a handshake this tab started, so a link someone was sent cannot quietly
 * complete a connection on their behalf.
 *
 * `sessionStorage`, not `localStorage`: it belongs to this tab and this visit.
 */
const STATE_KEY = "roofboard.gmail.oauth.state";

export function rememberState(): string {
  const state = crypto.randomUUID();
  sessionStorage.setItem(STATE_KEY, state);
  return state;
}

export function takeState(): string | null {
  const state = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return state;
}

/** What the server said, rather than "Edge Function returned a non-2xx". */
export class GmailError extends Error {
  needsConnection: boolean;
  refusal: string | null;

  constructor(
    message: string,
    options: { needsConnection?: boolean; refusal?: string | null } = {},
  ) {
    super(message);
    this.name = "GmailError";
    this.needsConnection = options.needsConnection ?? false;
    this.refusal = options.refusal ?? null;
  }
}

/**
 * Call one of the Gmail functions, keeping the message it sent back.
 *
 * `functions.invoke` reports a non-2xx as a generic error and puts the response
 * itself on `context`. The body is where the useful sentence is — "this
 * customer is blacklisted", "your Google account is not connected" — and
 * showing a rep "non-2xx status code" instead of that would make every failure
 * here look like the same unexplained one.
 */
async function invokeGmail<T>(
  name: "gmail-oauth" | "gmail-send" | "gmail-sync",
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) return data as T;

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const payload = await context.json().catch(() => null);
    if (payload && typeof payload.error === "string") {
      throw new GmailError(payload.error, {
        needsConnection: Boolean(payload.needsConnection),
        refusal: typeof payload.refusal === "string" ? payload.refusal : null,
      });
    }
  }
  throw new GmailError(error.message);
}

/** Send them to Google. Returns nothing — the page navigates away. */
export async function startGoogleConnect(): Promise<void> {
  const { url } = await invokeGmail<{ url: string }>("gmail-oauth", {
    action: "start",
    redirectUri: googleRedirectUri(),
    state: rememberState(),
  });
  window.location.assign(url);
}

export async function finishGoogleConnect(code: string): Promise<string> {
  const { email } = await invokeGmail<{ email: string }>("gmail-oauth", {
    action: "finish",
    code,
    redirectUri: googleRedirectUri(),
  });
  return email;
}

export async function disconnectGoogle(): Promise<void> {
  await invokeGmail("gmail-oauth", { action: "disconnect" });
}

export interface SendEmailInput {
  to: string;
  subject: string;
  /** Templates are plain text. `html` is for a caller with real markup. */
  text?: string;
  html?: string;
  cc?: string | null;
  replyTo?: string | null;
  jobId?: string | null;
  /** Given whenever there is one, because the flags live on the contact. */
  contactId?: string | null;
  /**
   * Staff to staff. Logged as internal, so the follow-up clock does not count
   * it as having reached the customer.
   */
  internal?: boolean | undefined;
  /**
   * Whose Gmail to send from, when it is not the caller's own.
   *
   * Only an admin may name somebody else, and the Edge Function is where that
   * is decided — see `resolveEmailSender` in packages/core. Sending this from
   * the browser is asking, not telling.
   */
  fromProfileId?: string | null;
  /**
   * Our own `email_threads.id`, when this is an answer inside a conversation
   * rather than a fresh letter. The Edge Function takes the subject and the
   * threading headers from the thread — see ADR 0041 — so a caller passing this
   * does not need to get `Re:` right, and cannot get it wrong.
   */
  threadId?: string | null;
  /**
   * Files to hang off the message.
   *
   * The storm history report goes to an adjuster as a link *and* as a file: the
   * link reads better and records when it was opened, the file drops into a
   * claim folder and survives a carrier's system flattening what it is sent.
   */
  attachments?: EmailAttachment[];
  /**
   * False for a letter that already closes with the rep's name and number —
   * the signed copy, a note tag — so it is not signed a second time by the
   * sender's Gmail signature underneath (ADR 0127). Absent is signed.
   */
  sign?: boolean;
}

export interface EmailAttachment {
  filename: string;
  mimeType: string;
  /** The file's bytes, base64 encoded. */
  content: string;
}

export async function sendEmail(
  input: SendEmailInput,
): Promise<{ id: string | null; threadId: string | null; from: string }> {
  return invokeGmail<{
    id: string | null;
    threadId: string | null;
    from: string;
  }>("gmail-send", { ...input });
}

/** One letter in a conversation, ours or the customer's. */
export interface ConversationMessage {
  id: string;
  direction: "inbound" | "outbound";
  from_address: string;
  from_name: string | null;
  subject: string | null;
  body_text: string | null;
  snippet: string | null;
  sent_at: string;
}

export interface Conversation {
  id: string;
  subject: string | null;
  last_message_at: string | null;
  email_messages: ConversationMessage[];
}

/**
 * Every conversation on a job, newest first, each with its letters oldest
 * first — which is the order a conversation is read in.
 */
export async function fetchConversations(
  jobId: string,
): Promise<Conversation[]> {
  const { data, error } = await supabase
    .from("email_threads")
    .select(
      "id, subject, last_message_at, email_messages(id, direction, from_address, from_name, subject, body_text, snippet, sent_at)",
    )
    .eq("job_id", jobId)
    .order("last_message_at", { ascending: false })
    .order("sent_at", { ascending: true, referencedTable: "email_messages" });
  if (error) throw new Error(error.message);
  return (data ?? []) as Conversation[];
}

/**
 * Ask Google whether anything new has been said.
 *
 * Deliberately quiet about failing. A reply that has not arrived yet is the
 * normal state of a conversation, and a red box every time Google is slow would
 * train everybody to ignore red boxes. What was already stored still reads, and
 * a connection that is genuinely broken says so on the Integrations screen.
 */
export async function syncConversations(jobId: string): Promise<number> {
  try {
    const result = await invokeGmail<{ added?: number }>("gmail-sync", {
      jobId,
    });
    return result?.added ?? 0;
  } catch {
    return 0;
  }
}

export async function fetchGmailAccounts(): Promise<GmailAccountRow[]> {
  const { data, error } = await supabase
    .from("gmail_accounts")
    .select(
      "profile_id, email_address, connected_at, last_sent_at, last_error, last_error_at",
    );
  if (error) throw new Error(error.message);
  return (data ?? []) as GmailAccountRow[];
}

/**
 * The scope that lets an inspection reach a calendar.
 *
 * Granted alongside `gmail.send` when somebody connects. Anybody who connected
 * before it was added holds a token that cannot touch a calendar, and Google
 * does not upgrade a token in place — so the Integrations screen reads the
 * scope off the row and asks them to connect again.
 */
export const CALENDAR_SCOPE = "https://www.googleapis.com/auth/calendar.events";

export const connectionDoesCalendar = (
  account: { scope?: string | null } | null | undefined,
): boolean => Boolean(account?.scope?.includes(CALENDAR_SCOPE));

/**
 * Put an inspection on a calendar, or take it off.
 *
 * Never throws and never blocks a save. The inspection is scheduled in Roof
 * Board either way and the Cal tab shows it; a calendar that would not take it
 * is worth reporting, not worth losing the appointment over. The Edge Function
 * writes the reason onto the job as well, so it survives this tab closing.
 */
/**
 * Put a crew's booked day on a calendar, or take it off.
 *
 * The same shape and the same promise as the inspection below: never throws,
 * never blocks a save. The work order is scheduled in Roof Board either way and
 * the Cal tab shows it; a calendar that would not take it is worth reporting,
 * not worth losing the booking over. The Edge Function writes the reason onto
 * the work order as well, so it survives this tab closing.
 *
 * Deliberately sends only the id. The function builds the event itself, because
 * `sendUpdates=all` makes Google mail a real invitation from a real CopperRidge
 * mailbox to whoever is on the attendee list.
 */
export async function syncCrewBookingToCalendar(input: {
  workOrderId: string;
  /** Whether the crew still has this day. False takes the event off. */
  booked: boolean;
}): Promise<{ ok: boolean; error?: string; uninvited?: string[] }> {
  try {
    const { data, error } = await supabase.functions.invoke("calendar-sync", {
      body: {
        kind: "work_order",
        action: input.booked ? "upsert" : "remove",
        workOrderId: input.workOrderId,
      },
    });
    if (error) return { ok: false, error: error.message };
    const result = data as {
      ok?: boolean;
      error?: string;
      uninvited?: string[];
    } | null;
    if (result?.ok) return { ok: true, uninvited: result.uninvited ?? [] };
    return { ok: false, error: result?.error ?? "The calendar refused it." };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export async function syncInspectionToCalendar(input: {
  jobId: string;
  /** Whether the inspection has a date. False unschedules it. */
  scheduled: boolean;
  minutes: number;
}): Promise<{ ok: boolean; error?: string; uninvited?: string[] }> {
  try {
    const { data, error } = await supabase.functions.invoke("calendar-sync", {
      body: {
        action: input.scheduled ? "upsert" : "remove",
        jobId: input.jobId,
        // Deliberately not the event itself. The Edge Function builds that from
        // the job row, because an event carries an attendee list and a body of
        // text that Google mails out from somebody's account — see the comment
        // above the build in calendar-sync/index.ts.
        minutes: input.minutes,
      },
    });
    if (error) return { ok: false, error: error.message };
    const result = data as {
      ok?: boolean;
      error?: string;
      uninvited?: string[];
    } | null;
    if (result?.ok) return { ok: true, uninvited: result.uninvited ?? [] };
    return { ok: false, error: result?.error ?? "The calendar refused it." };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
